<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("menu.app_catalog_categories") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <base-button
                        v-if="isAdmin"
                        size="md"
                        type="neutral"
                        @click="openCreate"
                    >
                        <i class="fa fa-plus"></i>
                        {{ $t("app_catalog.btn_add_category") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                <template slot="header">
                    <div class="row">
                        <div class="col-md-6">
                            <base-input
                                v-model="keyword"
                                prepend-icon="fas fa-search"
                                :placeholder="$t('search')"
                                @keyup.enter.native="loadList"
                            ></base-input>
                        </div>
                        <div class="col-md-2">
                            <base-button type="primary" size="sm" @click="loadList">
                                <i class="fa fa-search"></i> {{ $t("search") }}
                            </base-button>
                        </div>
                    </div>
                    <!-- Own muted line BELOW the toolbar (owner screenshot 2026-10-05:
                         the hint used to be pulled up into the search input). -->
                    <div class="row">
                        <div class="col-12">
                            <p class="text-muted text-sm mb-0 category-visibility-hint">{{ $t("app_catalog.visibility_hint") }}</p>
                        </div>
                    </div>
                </template>

                <div v-loading="loading">
                    <el-table
                        :data="list"
                        row-key="id"
                        header-row-class-name="thead-light"
                        :row-class-name="rowClassName"
                        :empty-text="$t('app_catalog.no_data')"
                    >
                        <!-- Column layout (owner 2026-10-05): no ID column; a big
                             thumbnail; the two names share almost all the width
                             (min-width only, no fixed width, so they stretch);
                             ลำดับ and the kebab are fixed-width and just fit. -->
                        <el-table-column :label="$t('app_catalog.col_image')" width="112" align="center">
                            <template slot-scope="{ row }">
                                <span class="category-dim">
                                    <img
                                        v-if="row.image"
                                        :src="row.image"
                                        class="category-thumb"
                                        alt=""
                                    />
                                    <span v-else class="text-muted">-</span>
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.col_name_th')" prop="name_th" min-width="260">
                            <template slot-scope="{ row }">
                                <span class="category-dim">{{ row.name_th }}</span>
                                <el-tag
                                    v-if="!combinedVisible(row)"
                                    size="mini"
                                    type="info"
                                    class="ml-2"
                                >{{ $t("app_catalog.visibility_hidden") }}</el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.col_name_en')" prop="name_en" min-width="260">
                            <template slot-scope="{ row }">
                                <span class="category-dim">{{ row.name_en }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('app_catalog.col_sort_order')" prop="sort_order" width="88" align="center"></el-table-column>
                        <!-- แสดง (owner follow-up 2026-10-05: "เปิด ปิด แค่ปุ่มเดียวพอ มีผล
                             ทั้ง 3 ที่"). ONE switch for เว็บ + สมาชิก + แอป: its value is
                             show_web && show_member && show_app, and a toggle PATCHes all
                             three flags together. Optimistic; a failure restores the three
                             previous flags; no table reload. Disabled until the list says
                             visibility_ready (SQL applied) — the PATCH would 503 before.
                             A row whose flags differ (set via API / legacy) reads OFF with a
                             "ตั้งค่าบางช่องทาง" tooltip; switching it on sets all three. -->
                        <el-table-column :label="$t('app_catalog.col_show')" width="88" align="center">
                            <template slot="header" slot-scope="scope">
                                <el-tooltip
                                    :content="$t(visibilityReady ? 'app_catalog.visibility_hint' : 'app_catalog.visibility_not_ready')"
                                    placement="top"
                                >
                                    <span>{{ $t("app_catalog.col_show") }} <i class="fa fa-info-circle text-muted"></i></span>
                                </el-tooltip>
                            </template>
                            <template slot-scope="{ row }">
                                <el-tooltip
                                    :disabled="!switchTooltip(row)"
                                    :content="switchTooltip(row)"
                                    placement="top"
                                >
                                    <el-switch
                                        :value="combinedVisible(row)"
                                        :disabled="!isAdmin || !visibilityReady || !!row.$toggling"
                                        @change="v => onToggleVisibility(row, v)"
                                    ></el-switch>
                                </el-tooltip>
                            </template>
                        </el-table-column>
                        <el-table-column v-if="isAdmin" :label="$t('actions')" width="72" align="center">
                            <template slot-scope="{ row }">
                                <!-- Same kebab as src/views/AppCatalog/Products.vue -->
                                <el-dropdown trigger="click" @command="cmd => onRowCommand(cmd, row)">
                                    <base-button
                                        size="sm"
                                        type="secondary"
                                        class="btn-icon-only"
                                        :title="$t('actions')"
                                        @click.native.stop
                                    >
                                        <i class="fa fa-ellipsis-v"></i>
                                    </base-button>
                                    <el-dropdown-menu slot="dropdown">
                                        <el-dropdown-item command="edit">
                                            <i class="fa fa-edit text-info mr-2"></i>{{ $t("app_catalog.edit_category") }}
                                        </el-dropdown-item>
                                        <el-dropdown-item command="delete" divided>
                                            <span class="text-danger">
                                                <i class="fa fa-trash mr-2"></i>{{ $t("app_catalog.btn_delete") }}
                                            </span>
                                        </el-dropdown-item>
                                    </el-dropdown-menu>
                                </el-dropdown>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </card>
        </div>

        <!-- Create / Edit Dialog -->
        <el-dialog
            :title="editMode ? $t('app_catalog.edit_category_title', { id: form.id }) : $t('app_catalog.btn_add_category')"
            :visible.sync="dialogVisible"
            width="600px"
            @closed="resetForm"
        >
            <el-form
                ref="categoryForm"
                :model="form"
                :rules="rules"
                label-width="130px"
            >
                <el-form-item :label="$t('app_catalog.col_name_th')" prop="name_th">
                    <el-input v-model="form.name_th"></el-input>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.col_name_en')" prop="name_en">
                    <el-input v-model="form.name_en"></el-input>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.col_sort_order')">
                    <el-input-number v-model="form.sort_order" :min="0"></el-input-number>
                </el-form-item>
                <el-form-item :label="$t('app_catalog.col_image')">
                    <s3-image-upload
                        object-type="category"
                        v-model="form.image_cdn_url"
                        large
                        @update:objectKey="form.image_key = $event"
                    ></s3-image-upload>
                </el-form-item>
                <!-- One checkbox for all three channels. A mixed row (flags differ)
                     reads unticked and is saved back unchanged unless the box is
                     touched; ticking/unticking sets all three. -->
                <el-form-item v-if="visibilityReady" :label="$t('app_catalog.col_show')">
                    <el-checkbox
                        :value="combinedVisible(form)"
                        @change="v => setFormVisibility(v)"
                    >{{ channelNames }}</el-checkbox>
                </el-form-item>
            </el-form>
            <span slot="footer" class="dialog-footer">
                <base-button type="secondary" @click="dialogVisible = false">{{ $t("cancel") || "Cancel" }}</base-button>
                <base-button type="primary" :loading="saving" @click="submitForm">{{ $t("save") }}</base-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import { apiErrorMessage } from "@/util/api-error";
import { mapGetters, mapActions } from "vuex";
import swal from "sweetalert2";
import {
    Table,
    TableColumn,
    Dialog,
    Form,
    FormItem,
    Input,
    InputNumber,
    Dropdown,
    DropdownMenu,
    DropdownItem,
    Switch,
    Tooltip,
    Tag,
    Checkbox
} from "element-ui";
import S3ImageUpload from "@/components/Inputs/S3ImageUpload.vue";
// The API stores an object KEY, never a signed URL (signed-image spec §3.3 #4),
// so an older row that only has the URL has its key derived here — with the
// signature stripped first. Pure + unit-tested in tests/unit/cdnImage.spec.js.
import { cdnObjectKey } from "@/utils/cdnImage";
// Channel visibility rules (which flags, single-flag PATCH body, hidden-
// everywhere, undefined ⇒ shown). Pure + unit-tested in
// tests/unit/categoryVisibility.spec.js.
import {
    VISIBILITY_FIELDS,
    VISIBILITY_LABEL_KEYS,
    allChannelsPayload,
    combinedVisible,
    isMixed,
    readVisibility,
    defaultVisibility,
    visibilityFromResponse
} from "@/utils/categoryVisibility";

export default {
    name: "AppCatalogCategories",
    components: {
        S3ImageUpload,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Dialog.name]: Dialog,
        [Form.name]: Form,
        [FormItem.name]: FormItem,
        [Input.name]: Input,
        [InputNumber.name]: InputNumber,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem,
        [Switch.name]: Switch,
        [Tooltip.name]: Tooltip,
        [Tag.name]: Tag,
        [Checkbox.name]: Checkbox
    },
    data() {
        return {
            keyword: "",
            dialogVisible: false,
            editMode: false,
            saving: false,
            form: {
                name_th: "",
                name_en: "",
                sort_order: 0,
                image_cdn_url: "",
                image_key: "",
                ...defaultVisibility()
            },
            rules: {
                name_th: [{ required: true, message: this.$t("app_catalog.field_required"), trigger: "blur" }]
            }
        };
    },
    computed: {
        ...mapGetters({
            list: "AppCatalogCategory/list",
            loading: "AppCatalogCategory/loading",
            visibilityReady: "AppCatalogCategory/visibilityReady",
            isAdmin: "Auth/isAdmin"
        }),
        // "เว็บ · สมาชิก · แอป" — what the single dialog checkbox covers.
        channelNames() {
            return VISIBILITY_FIELDS.map(f => this.$t(VISIBILITY_LABEL_KEYS[f])).join(" · ");
        }
    },
    mounted() {
        this.loadList();
    },
    methods: {
        ...mapActions({
            fetchList: "AppCatalogCategory/fetchList",
            createItem: "AppCatalogCategory/create",
            updateItem: "AppCatalogCategory/update",
            removeItem: "AppCatalogCategory/remove",
            setVisibility: "AppCatalogCategory/setVisibility"
        }),
        loadList() {
            this.fetchList({ keyword: this.keyword });
        },
        onRowCommand(cmd, row) {
            if (cmd === "edit") return this.openEdit(row);
            if (cmd === "delete") return this.confirmDelete(row);
        },
        openCreate() {
            this.editMode = false;
            this.dialogVisible = true;
        },
        openEdit(row) {
            this.editMode = true;
            this.form = {
                id: row.id,
                name_th: row.name_th || "",
                name_en: row.name_en || "",
                sort_order: row.sort_order || 0,
                image_cdn_url: row.image || "",
                // An older row carries only `image` (the resolved URL). Saving it
                // back with an empty key would lose the picture once the API stops
                // accepting a URL in its place, and the URL now ends in a
                // `?e=&s=&k=` signature that is no part of the object key.
                image_key: row.image_key || cdnObjectKey(row.image),
                ...readVisibility(row)
            };
            this.dialogVisible = true;
        },
        resetForm() {
            this.form = {
                name_th: "",
                name_en: "",
                sort_order: 0,
                image_cdn_url: "",
                image_key: "",
                ...defaultVisibility()
            };
            if (this.$refs.categoryForm) {
                this.$refs.categoryForm.resetFields();
            }
        },
        submitForm() {
            this.$refs.categoryForm.validate(async (valid) => {
                if (!valid) return;
                this.saving = true;
                try {
                    const payload = {
                        name_th: this.form.name_th,
                        name_en: this.form.name_en,
                        sort_order: this.form.sort_order,
                        image_cdn_url: this.form.image_cdn_url,
                        image_key: this.form.image_key,
                        // Ignored by the BE until the SQL is applied (spec §4.2).
                        show_web: this.form.show_web,
                        show_member: this.form.show_member,
                        show_app: this.form.show_app
                    };
                    if (this.editMode) {
                        await this.updateItem({ id: this.form.id, payload });
                    } else {
                        await this.createItem(payload);
                    }
                    this.dialogVisible = false;
                    this.loadList();
                    this.$notify({ type: "success", message: this.$t("app_catalog.save_success") });
                } catch (err) {
                    const msg = apiErrorMessage(err, this.$t);
                    this.$notify({ type: "danger", message: msg });
                } finally {
                    this.saving = false;
                }
            });
        },
        combinedVisible(row) {
            return combinedVisible(row);
        },
        rowClassName({ row }) {
            return combinedVisible(row) ? "" : "category-row--hidden";
        },
        // Tooltip on the switch: not-ready wins; then the mixed-flags note; else none.
        switchTooltip(row) {
            if (!this.visibilityReady) return this.$t("app_catalog.visibility_not_ready");
            if (isMixed(row)) return this.$t("app_catalog.visibility_mixed");
            return "";
        },
        setFormVisibility(value) {
            const flags = allChannelsPayload(value);
            Object.keys(flags).forEach(f => this.$set(this.form, f, flags[f]));
        },
        // Optimistic write (pattern Products.vue onToggleAppEnable): all three
        // flags move first, ONE PATCH carries all three, a failure restores the
        // three previous values with a red toast. No list reload on either path.
        // `$toggling` keeps a double click from sending two opposite writes.
        async onToggleVisibility(row, value) {
            if (!this.isAdmin || !this.visibilityReady || row.$toggling) return;
            const previous = readVisibility(row);
            const payload = allChannelsPayload(value);
            Object.keys(payload).forEach(f => this.$set(row, f, payload[f]));
            this.$set(row, "$toggling", true);
            try {
                const res = await this.setVisibility({ id: row.id, payload });
                // The response is the source of truth for all three flags.
                const flags = visibilityFromResponse(res);
                Object.keys(flags).forEach(f => this.$set(row, f, flags[f]));
                this.$notify({ type: "success", message: this.$t("app_catalog.visibility_saved") });
            } catch (err) {
                Object.keys(previous).forEach(f => this.$set(row, f, previous[f]));
                this.$notify({ type: "danger", message: this.$t("app_catalog.visibility_failed") });
            } finally {
                this.$set(row, "$toggling", false);
            }
        },
        confirmDelete(row) {
            swal.fire({
                title: this.$t("app_catalog.delete_title"),
                text: this.$t("app_catalog.delete_msg"),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                focusCancel: true,
                confirmButtonText: this.$t("app_catalog.btn_delete"),
                cancelButtonText: this.$t("app_catalog.btn_cancel"),
                confirmButtonColor: "#d33",
                cancelButtonColor: "#6c757d"
            }).then(async (result) => {
                if (result.value) {
                    try {
                        await this.removeItem(row.id);
                        this.loadList();
                        this.$notify({ type: "success", message: this.$t("app_catalog.delete_success") });
                    } catch (err) {
                        const msg = apiErrorMessage(err, this.$t);
                        this.$notify({ type: "danger", message: msg });
                    }
                }
            });
        }
    }
};
</script>

<style scoped>
/* Hidden (switch off): mute the image + names, NOT the switch, so turning it
   back on stays obvious. */
.category-row--hidden .category-dim {
    opacity: 0.55;
}
.category-thumb {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 6px;
}
</style>
